export default function PrivacyPolicy() {
  return (
    <div className="pt-[73px]">
      {/* Header Section */}
      <section className="bg-primary text-white py-16 relative overflow-hidden">
        <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12 text-center space-y-4">
          <span className="text-accent text-xs font-bold tracking-widest uppercase">Legal</span>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">
            Privacy Policy
          </h1>
          <p className="text-slate-300 max-w-2xl mx-auto text-sm">
            Effective date: 2024-01-19 | Updated on: 2024-01-19
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-4xl px-6 md:px-12 space-y-8 text-slate-600 text-sm leading-relaxed">
          <div className="space-y-4">
            <p>
              This Privacy Policy explains the policies of Site Facility ApS on the collection and use of the information we collect when you access <a href="https://sitefacility.dk/" className="text-accent hover:underline">https://sitefacility.dk/</a> (the “Service”). This Privacy Policy describes your privacy rights and how you are protected under privacy laws.
            </p>
            <p>
              By using our Service, you are consenting to the collection and use of your information in accordance with this Privacy Policy. Please do not access or use our Service if you do not consent to the collection and use of your information as outlined in this Privacy Policy. This Privacy Policy has been created with the help of CookieScript Privacy Policy Generator.
            </p>
            <p>
              Site Facility ApS is authorized to modify this Privacy Policy at any time. This may occur without prior notice. Site Facility ApS will post the revised Privacy Policy on the <a href="https://sitefacility.dk/" className="text-accent hover:underline">https://sitefacility.dk/</a> website.
            </p>
          </div>

          <div className="space-y-4 border-t border-slate-100 pt-6">
            <h2 className="text-lg font-bold text-primary uppercase tracking-wider">Collection and Use of Your Personal Information</h2>
            <h3 className="font-bold text-primary">Information We Collect</h3>
            <p>
              When using our Service, you will be prompted to provide us with personal information used to contact or identify you. <a href="https://sitefacility.dk/" className="text-accent hover:underline">https://sitefacility.dk/</a> collects the following information:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Usage Data</li>
              <li>Name</li>
              <li>Email</li>
              <li>Mobile Number</li>
              <li>Work Address</li>
              <li>Home Address</li>
            </ul>
            <p className="font-semibold text-primary mt-4">Usage Data includes the following:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Internet Protocol (IP) address of computers accessing the site</li>
              <li>Web page requests</li>
              <li>Referring web pages</li>
              <li>Browser used to access site</li>
              <li>Time and date of access</li>
            </ul>
          </div>

          <div className="space-y-4 border-t border-slate-100 pt-6">
            <h2 className="text-lg font-bold text-primary uppercase tracking-wider">How We Collect Information</h2>
            <p>
              <a href="https://sitefacility.dk/" className="text-accent hover:underline">https://sitefacility.dk/</a> collects and receives information from you in the following manner:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>When you fill a registration form or otherwise submit your personal information.</li>
              <li>When you interact with our Service.</li>
            </ul>
            <p>
              Your information will be stored for up to 30 days after it is no longer required to provide you the services. Your information may be retained for longer periods for reporting or record-keeping in accordance with applicable laws. Information which does not identify you personally may be stored indefinitely.
            </p>
          </div>

          <div className="space-y-4 border-t border-slate-100 pt-6">
            <h2 className="text-lg font-bold text-primary uppercase tracking-wider">How We Use Your Information</h2>
            <p>
              <a href="https://sitefacility.dk/" className="text-accent hover:underline">https://sitefacility.dk/</a> may use your information for the following purposes:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Providing and maintaining our Service</strong>, as well as monitoring the usage of our Service.</li>
              <li><strong>For other purposes:</strong> Site Facility ApS will use your information for data analysis to identify usage trends or determine the effectiveness of our marketing campaigns when reasonable. We will use your information to evaluate and improve our Service, products, services, and marketing efforts.</li>
              <li><strong>To contact you:</strong> Site Facility ApS will contact you by email, phone, SMS, or another form of electronic communication related to the functions, products, services, or security updates when necessary or reasonable.</li>
            </ul>
          </div>

          <div className="space-y-4 border-t border-slate-100 pt-6">
            <h2 className="text-lg font-bold text-primary uppercase tracking-wider">How We Share Your Information</h2>
            <p>
              Site Facility ApS will share your information, when applicable, in the following situations:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>With your consent:</strong> Site Facility ApS will share your information for any purpose with your explicit consent.</li>
              <li><strong>For transfers of business:</strong> Your information will be shared in the event of connection, negotiation, merger, sale of business assets, financing, or acquisition of all or a portion of our business with another company.</li>
            </ul>
          </div>

          <div className="space-y-4 border-t border-slate-100 pt-6">
            <h2 className="text-lg font-bold text-primary uppercase tracking-wider">Third-Party Sharing</h2>
            <p>
              Any third party we share your information with must disclose the purpose for which they intend to use your information. They must retain your information only for the duration disclosed when requesting or receiving said information. The third-party service provider must not further collect, sell, or use your personal information except as necessary to perform the specified purpose.
            </p>
            <p>Your information may be shared to a third-party for reasons including:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Analytics information:</strong> Your information might be shared with online analytics tools in order to track and analyse website traffic.</li>
              <li><strong>Improving targeted advertising campaigns:</strong> Site Facility ApS partners with third party service providers to improve targeted advertising campaigns.</li>
            </ul>
            <p>
              If you choose to provide such information during registration or otherwise, you are giving Site Facility ApS permission to use, share, and store that information in a manner consistent with this Privacy Policy.
            </p>
            <p>Your information may be disclosed for additional reasons, including:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Complying with applicable laws, regulations, or court orders.</li>
              <li>Responding to claims that your use of our Service violates third-party rights.</li>
              <li>Enforcing agreements you make with us, including this Privacy Policy.</li>
            </ul>
          </div>

          <div className="space-y-4 border-t border-slate-100 pt-6">
            <h2 className="text-lg font-bold text-primary uppercase tracking-wider">Cookies</h2>
            <p>
              Cookies are small text files that are placed on your computer by websites that you visit. Websites use cookies to help users navigate efficiently and perform certain functions. Cookies that are required for the website to operate properly are allowed to be set without your permission. All other cookies need to be approved before they can be set in the browser.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Strictly necessary cookies:</strong> Strictly necessary cookies allow core website functionality such as user login and account management. The website cannot be used properly without strictly necessary cookies.</li>
            </ul>
          </div>

          <div className="space-y-4 border-t border-slate-100 pt-6">
            <h2 className="text-lg font-bold text-primary uppercase tracking-wider">Security</h2>
            <p>
              Your information’s security is important to us. <a href="https://sitefacility.dk/" className="text-accent hover:underline">https://sitefacility.dk/</a> utilizes a range of security measures to prevent the misuse, loss, or alteration of the information you have given us. However, because we cannot guarantee the security of the information you provide us, you must access our service at your own risk.
            </p>
            <p>
              Site Facility ApS is not responsible for the performance of websites operated by third parties or your interactions with them. When you leave this website, we recommend you review the privacy practices of other websites you interact with and determine the adequacy of those practices.
            </p>
          </div>

          <div className="space-y-4 border-t border-slate-100 pt-6">
            <h2 className="text-lg font-bold text-primary uppercase tracking-wider">Contact Us</h2>
            <p>For any questions, please contact us through the following methods:</p>
            <ul className="list-none space-y-2 pl-0">
              <li><strong>Name:</strong> Site Facility ApS</li>
              <li><strong>Address:</strong> Vestkraftgade 1, 2nd Floor, 6700 Esbjerg, Denmark</li>
              <li><strong>Email:</strong> <a href="mailto:info@sitefacility.dk" className="text-accent hover:underline">info@sitefacility.dk</a></li>
              <li><strong>Phone:</strong> <a href="tel:+4521795753" className="text-accent hover:underline">+45 21 79 57 53</a></li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
